<template>
  <div class="border-b border-surface-light/20 bg-surface/40 px-4 py-4">
    <h2 class="mb-3 text-2xl font-extrabold tracking-tight text-fg">
      {{ t('searchGameTitle') }}
    </h2>

    <div class="flex flex-wrap items-center gap-3">
      <!-- Provider 下拉（typeahead，外觀像 select）-->
      <div class="relative w-full sm:w-60">
        <input
          v-model="providerQuery"
          type="text"
          :placeholder="currentProvider ? providerDisplayName(currentProvider.provider, auth.locale) : t('selectProvider')"
          class="w-full rounded-lg border border-primary/40 bg-surface/60 px-3 py-2 pr-9 text-sm text-fg placeholder:text-fg-subtle focus:border-primary-light focus:outline-none focus:ring-1 focus:ring-primary-light"
          @focus="providerOpen = true"
          @blur="providerOpen = false"
        >
        <!-- 有輸入字 → ✕ 清除；否則 → ▾ chevron -->
        <button
          v-if="providerQuery"
          type="button"
          class="absolute right-2.5 top-1/2 -translate-y-1/2 text-fg-subtle transition-colors hover:text-fg"
          :aria-label="t('clear')"
          @mousedown.prevent="providerQuery = ''"
        >
          ✕
        </button>
        <svg
          v-else
          class="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-subtle"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          stroke-width="1.6"
        >
          <path
            d="M6 8l4 4 4-4"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>

        <ul
          v-if="providerOpen && filteredProviders.length"
          class="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-lg bg-surface shadow-lg ring-1 ring-surface-light/30"
        >
          <li
            v-for="p in filteredProviders"
            :key="p.id"
            class="cursor-pointer px-3 py-2 text-sm text-fg hover:bg-surface-light/20"
            @mousedown.prevent="selectProvider(p)"
          >
            <ProviderName :provider="p.provider" />
          </li>
        </ul>
      </div>

      <!-- 遊戲搜尋（放大鏡 icon + ✕ 清除）-->
      <div class="relative min-w-0 flex-1">
        <svg
          class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-subtle"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          stroke-width="1.6"
        >
          <circle
            cx="9"
            cy="9"
            r="6"
          />
          <path
            d="M14 14l3.5 3.5"
            stroke-linecap="round"
          />
        </svg>
        <input
          v-model="gameQuery"
          type="text"
          :disabled="!currentProvider"
          :placeholder="currentProvider ? t('searchGameId') : t('searchGameIdDisabled')"
          class="w-full rounded-lg border border-primary/40 bg-surface/60 px-3 py-2 pl-9 pr-9 text-sm text-fg placeholder:text-fg-subtle focus:border-primary-light focus:outline-none focus:ring-1 focus:ring-primary-light disabled:cursor-not-allowed disabled:opacity-40"
        >
        <button
          v-if="gameQuery"
          type="button"
          class="absolute right-2.5 top-1/2 -translate-y-1/2 text-fg-subtle transition-colors hover:text-fg"
          :aria-label="t('clear')"
          @mousedown.prevent="clearGameSearch"
        >
          ✕
        </button>

        <ul
          v-if="gameResults.length"
          class="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-lg bg-surface shadow-lg ring-1 ring-surface-light/30"
        >
          <li
            v-for="g in gameResults"
            :key="g.id"
            class="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-fg hover:bg-surface-light/20"
            @mousedown.prevent="launchSearchGame(g)"
          >
            <img
              v-if="g.image"
              :src="g.image"
              class="h-8 w-8 shrink-0 rounded object-cover"
              @error="(e) => (e.target.style.display = 'none')"
            >
            <span class="min-w-0 flex-1">
              <span class="block truncate">{{ g.title || g.game_id }}</span>
              <span class="block truncate text-xs text-fg-subtle">ID: {{ g.game_id }}</span>
            </span>
            <span class="shrink-0 text-xs text-fg-subtle">{{ g.category_name }}</span>
          </li>
        </ul>
        <div
          v-else-if="gameSearched && gameQuery.trim().length >= 2"
          class="absolute z-20 mt-1 w-full rounded-lg bg-surface px-3 py-2 text-xs text-fg-subtle ring-1 ring-surface-light/30"
        >
          {{ t('noResults') }}
        </div>
      </div>

      <!-- Reset：清空搜尋並回到未選狀態 -->
      <button
        type="button"
        class="shrink-0 rounded-lg bg-surface px-4 py-2 text-sm font-medium text-fg-muted ring-1 ring-surface-light/40 transition-colors hover:bg-surface-light/20 hover:text-fg"
        @click="resetAll"
      >
        {{ t('reset') }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import api from '~/player/api/client'
import { useAuthStore } from '~/player/stores/auth'
import { useGameNavStore } from '~/player/stores/gameNav'
import { useI18n } from '~/player/i18n'
import { providerDisplayName } from '~/shared/utils/providerBranding'
import ProviderName from '~/player/components/ProviderName.vue'

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const nav = useGameNavStore()
const { t } = useI18n(auth)

const providerQuery = ref('')
const providerOpen = ref(false)
const gameResults = ref([])
const gameSearched = ref(false)

// 查詢字串存在 store（綁 provider），離開遊戲頁返回時可還原
const gameQuery = computed({
  get: () => nav.gameQuery,
  set: (v) => nav.setGameQuery(v, route.params.id)
})

onMounted(async () => {
  await nav.ensureProviders()
  // 返回同一 provider 且有舊查詢 → 重跑搜尋還原結果
  const id = route.params.id
  if (id && String(nav.gameQueryProviderId) === String(id) && nav.gameQuery) {
    runGameSearch(nav.gameQuery)
  }
})

const currentProvider = computed(() => {
  const id = route.params.id
  if (!id) return null
  return nav.providers.find((p) => String(p.id) === String(id)) || null
})

const filteredProviders = computed(() => {
  const kw = providerQuery.value.trim().toLowerCase()
  if (!kw) return nav.providers
  return nav.providers.filter((p) => {
    const raw = (p.provider || '').toLowerCase()
    const name = providerDisplayName(p.provider, auth.locale).toLowerCase()
    return raw.includes(kw) || name.includes(kw)
  })
})

function selectProvider(p) {
  providerOpen.value = false
  providerQuery.value = ''
  router.push(`/providers/${p.id}`)
}

function clearGameSearch() {
  nav.clearGameQuery()
  gameResults.value = []
  gameSearched.value = false
}

// reset：清空全部搜尋，並回到未選 provider 的首頁
function resetAll() {
  providerQuery.value = ''
  providerOpen.value = false
  clearGameSearch()
  if (route.params.id) router.push('/')
}

// 實際打 API 取搜尋結果（debounce 與還原都呼叫它）
async function runGameSearch(kw) {
  const q = (kw || '').trim()
  if (!currentProvider.value || q.length < 2) {
    gameResults.value = []
    gameSearched.value = false
    return
  }
  try {
    const { data } = await api.get(`/providers/${route.params.id}/games`, {
      params: { q, locale: auth.locale }
    })
    gameResults.value = data.data
    gameSearched.value = true
  } catch {
    gameResults.value = []
    gameSearched.value = true
  }
}

// 路由切換：去遊戲頁（無 provider id）保留查詢以利返回還原；換到不同 provider 才清空，回到同 provider 則還原
watch(
  () => route.params.id,
  (id) => {
    if (!id) return
    if (String(nav.gameQueryProviderId) !== String(id)) {
      clearGameSearch()
    } else if (nav.gameQuery) {
      runGameSearch(nav.gameQuery)
    }
  }
)

let debounceTimer = null
watch(gameQuery, (val) => {
  window.clearTimeout(debounceTimer)
  const kw = (val || '').trim()
  if (!currentProvider.value || kw.length < 2) {
    gameResults.value = []
    gameSearched.value = false
    return
  }
  debounceTimer = window.setTimeout(() => runGameSearch(kw), 300)
})

function launchSearchGame(g) {
  router.push({
    path: `/play/${g.id}`,
    query: {
      title: g.title,
      game_id: g.game_id,
      provider: g.provider_name,
      from_provider: route.params.id
    }
  })
}
</script>
